import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, ArrowUpRight, CaretDown } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { AUDIENCES } from "../data/audiences";

const ABOUT_LINKS = [
  { to: "/about", label: "About Anxya", desc: "The company, principles and AI-native difference" },
  { to: "/story", label: "Our Story", desc: "Karma Sanyas, Vasudhaiva Kutumbakam and patients first" },
  { to: "/why-anxya", label: "Why Anxya", desc: "The 6 Ds, 3 Ps and the agentic-era advantage" },
];

const LINKS = [
  { to: "/partners", label: "Partners" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact Us" },
  { to: "/ekam/login", label: "Sign In" },
];

const isAboutPath = (pathname) => ["/about", "/story", "/why-anxya"].includes(pathname);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobAbout, setMobAbout] = useState(false);
  const [audOpen, setAudOpen] = useState(false);
  const [mobAud, setMobAud] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-primary/10 bg-white/70 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group" onClick={() => setOpen(false)}>
          <img src="/assets/logo-icon.png" alt="Anxya Tech logo" className="h-9 w-9 object-contain transition-transform duration-300 group-hover:scale-110" />
          <span className="font-display font-semibold tracking-tight text-primary text-lg">
            Anxya<span className="text-brand-700">Tech</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-5" data-testid="nav-desktop">
          <div className="relative" onMouseEnter={() => setAboutOpen(true)} onMouseLeave={() => setAboutOpen(false)}>
            <button
              data-testid="nav-about-toggle"
              className={`flex items-center gap-1 whitespace-nowrap text-sm font-medium transition-colors hover:text-brand-700 ${isAboutPath(pathname) ? "text-brand-700" : "text-primary/80"}`}
            >
              About <CaretDown size={14} weight="bold" className={`transition-transform duration-300 ${aboutOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {aboutOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
                  data-testid="nav-about-menu"
                >
                  <div className="w-80 rounded-2xl border border-primary/10 bg-white/95 backdrop-blur-xl shadow-xl shadow-primary/10 p-4">
                    {ABOUT_LINKS.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        data-testid={`nav-about-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setAboutOpen(false)}
                        className="block rounded-lg px-3 py-2.5 hover:bg-muted/70 transition-colors"
                      >
                        <p className="text-sm font-semibold text-primary">{item.label}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{item.desc}</p>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink
            to="/universal-ai-os"
            data-testid="nav-platform-universal-link"
            className={({ isActive }) => `whitespace-nowrap text-sm font-medium transition-colors hover:text-brand-700 ${isActive ? "text-brand-700" : "text-primary/80"}`}
          >
            Anxya AI OS & PaaS
          </NavLink>

          <div className="relative" onMouseEnter={() => setAudOpen(true)} onMouseLeave={() => setAudOpen(false)}>
            <button
              data-testid="nav-audiences-toggle"
              className={`flex items-center gap-1 whitespace-nowrap text-sm font-medium transition-colors hover:text-brand-700 ${pathname.startsWith("/audiences") ? "text-brand-700" : "text-primary/80"}`}
            >
              Who We Serve <CaretDown size={14} weight="bold" className={`transition-transform duration-300 ${audOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {audOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
                  data-testid="nav-audiences-menu"
                >
                  <div className="w-72 rounded-2xl border border-primary/10 bg-white/95 backdrop-blur-xl shadow-xl shadow-primary/10 p-4">
                    {AUDIENCES.map((a) => (
                      <Link
                        key={a.slug}
                        to={`/audiences/${a.slug}`}
                        data-testid={`nav-audience-${a.slug}`}
                        onClick={() => setAudOpen(false)}
                        className="rounded-lg px-3 py-2.5 hover:bg-muted/70 transition-colors block"
                      >
                        <p className="text-sm font-semibold text-primary">{a.name}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{a.tagline}</p>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium whitespace-nowrap transition-colors hover:text-brand-700 ${isActive ? "text-brand-700" : "text-primary/80"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/demo"
            data-testid="nav-book-demo-btn"
            className="whitespace-nowrap inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
          >
            Talk to Us <ArrowUpRight size={16} weight="bold" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-primary p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-primary/10 bg-white/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <div>
                <button
                  data-testid="nav-mobile-about-toggle"
                  onClick={() => setMobAbout(!mobAbout)}
                  className={`flex w-full items-center justify-between text-lg font-display font-medium ${isAboutPath(pathname) ? "text-brand-700" : "text-primary"}`}
                >
                  About <CaretDown size={18} weight="bold" className={`transition-transform duration-300 ${mobAbout ? "rotate-180" : ""}`} />
                </button>
                {mobAbout && (
                  <div className="mt-3 ml-1 flex flex-col gap-3 border-l-2 border-accent/30 pl-4">
                    {ABOUT_LINKS.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        data-testid={`nav-mobile-about-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-base text-primary/80"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/universal-ai-os"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-platform-universal-link"
                className={`text-lg font-display font-medium ${pathname === "/universal-ai-os" ? "text-brand-700" : "text-primary"}`}
              >
                Anxya AI OS & PaaS
              </Link>

              <div>
                <button
                  data-testid="nav-mobile-audiences-toggle"
                  onClick={() => setMobAud(!mobAud)}
                  className={`flex w-full items-center justify-between text-lg font-display font-medium ${pathname.startsWith("/audiences") ? "text-brand-700" : "text-primary"}`}
                >
                  Who We Serve <CaretDown size={18} weight="bold" className={`transition-transform duration-300 ${mobAud ? "rotate-180" : ""}`} />
                </button>
                {mobAud && (
                  <div className="mt-3 ml-1 flex flex-col gap-3 border-l-2 border-accent/30 pl-4">
                    {AUDIENCES.map((a) => (
                      <Link
                        key={a.slug}
                        to={`/audiences/${a.slug}`}
                        onClick={() => setOpen(false)}
                        data-testid={`nav-mobile-audience-${a.slug}`}
                        className="text-base text-primary/80"
                      >
                        {a.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={`text-lg font-display font-medium ${pathname === l.to ? "text-brand-700" : "text-primary"}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/demo"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-book-demo-btn"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white"
              >
                Talk to Us <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
